import { inject, Injectable } from '@angular/core';
import {
    AppModule,
    ModuleRoutes,
    ModuleGroups,
    AgingRoutes,
    WorklistRoutes,
    AdminRoutes,
    ScrubbingRoutes,
    ScrubbingClaimsRoutes,
    ScrubbingUploadClaimsRoutes,
    ScrubbingWorklistRoutes,
    PostingWorklistRoutes,
    TodoRoutes,
    NotificationRoutes,
} from '@core/constants/module-history.constants';
import { ModuleHistoryStore } from '@core/stores/module-history.store';
import { Router } from '@angular/router';
import { getState } from '@ngrx/signals';
import { cloneDeep, trimEnd, trimStart } from 'lodash-es';
import { TableStore } from '@rcp/common-web';
import { AuthService } from '@services/auth/auth.service';
import { BehaviorSubject } from 'rxjs';
import { UserSessionResponse } from '@core/generated/openapi';
import { PM_SYSTEM_IDS } from '@core/constants/external-system.constants';
import { TabsStore } from '@core/stores/tabs.store';
import { StateClearService } from '@services/state/state-clear.service';
import { USER_ROLES } from '@core/constants/application.constants';
import { FlagsService } from '@services/flags/flags.service';
import { FEATURE_FLAGS } from '@core/constants/flags.constants';

@Injectable({
    providedIn: 'root',
})
export class ModuleHistoryService {
    // APP_MODULES represents all routes in the app that we want to use for module history or active module tracking.
    private readonly APP_MODULES: AppModule[] = [
        {
            route: ModuleRoutes.PostingWorklist,
            showNewLabel: true,
            label: 'Posting',
            icon: 'fa-receipt',
            group: ModuleGroups.Modules,
            defaultChildRoute: 'all',
            restrictUserAccess: (u: UserSessionResponse) =>
                !this.flagService.isFlagActive(
                    FEATURE_FLAGS.POSTING_WORKLIST_TAB,
                ),
            children: [
                {
                    route: PostingWorklistRoutes.All,
                    label: 'Worklist',
                },
            ],
        },
        {
            route: ModuleRoutes.Scrubbing,
            label: 'Scrubbing',
            icon: 'fa-bolt',
            group: ModuleGroups.Modules,
            defaultChildRoute: ScrubbingRoutes.Claims,
            restrictUserAccess: (u: UserSessionResponse) =>
                u.pmSystem?.id === PM_SYSTEM_IDS.EYE_CLOUD_PRO,
            children: [
                // {
                //     route: ScrubbingRoutes.Summary,
                //     label: 'Summary',
                //     icon: 'fa-duotone fa-dollar-sign',
                // },
                // {
                //     icon: 'fa-duotone fa-list-check',
                //     route: ScrubbingRoutes.Worklist,
                //     label: 'Worklist',
                //     defaultChildRoute: ScrubbingWorklistRoutes.All,
                //     children: [
                //         {
                //             route: ScrubbingWorklistRoutes.Todo,
                //         },
                //         {
                //             route: ScrubbingWorklistRoutes.Notifications,
                //         },
                //         {
                //             route: ScrubbingWorklistRoutes.Issues,
                //         },
                //     ],
                // },
                {
                    route: ScrubbingRoutes.Claims,
                    label: 'Worklist',
                    defaultChildRoute: ScrubbingClaimsRoutes.ReadyToFile,
                    restrictUserAccess: (u: UserSessionResponse) =>
                        u.pmSystem?.id === PM_SYSTEM_IDS.EYE_CLOUD_PRO,
                    children: [
                        {
                            route: ScrubbingClaimsRoutes.ReadyToFile,
                            restrictUserAccess: (u: UserSessionResponse) =>
                                u.pmSystem?.id === PM_SYSTEM_IDS.EYE_CLOUD_PRO,
                        },
                        {
                            route: ScrubbingClaimsRoutes.NeedsReview,
                            restrictUserAccess: (u: UserSessionResponse) =>
                                u.pmSystem?.id === PM_SYSTEM_IDS.EYE_CLOUD_PRO,
                        },
                        {
                            route: ScrubbingClaimsRoutes.OnHold,
                            restrictUserAccess: (u: UserSessionResponse) =>
                                u.pmSystem?.id === PM_SYSTEM_IDS.EYE_CLOUD_PRO,
                        },
                        {
                            route: ScrubbingClaimsRoutes.All,
                            restrictUserAccess: (u: UserSessionResponse) =>
                                u.pmSystem?.id === PM_SYSTEM_IDS.EYE_CLOUD_PRO,
                        },
                        {
                            route: ScrubbingClaimsRoutes.SearchClaims,
                            restrictUserAccess: (u: UserSessionResponse) =>
                                u.pmSystem?.id === PM_SYSTEM_IDS.EYE_CLOUD_PRO,
                        },
                    ],
                },
                {
                    route: ScrubbingRoutes.UploadClaims,
                    label: 'Files',
                    defaultChildRoute: ScrubbingUploadClaimsRoutes.Summary,
                    restrictUserAccess: (u: UserSessionResponse) =>
                        u.pmSystem?.id === PM_SYSTEM_IDS.EYE_CLOUD_PRO,
                    children: [
                        {
                            route: ScrubbingUploadClaimsRoutes.Summary,
                        },
                    ],
                },
                {
                    route: ScrubbingRoutes.Insights,
                    label: 'Insights',
                    defaultChildRoute: 'all',
                },
            ],
        },
        {
            route: ModuleRoutes.Aging,
            label: 'Aging',
            icon: 'fa-dollar',
            group: ModuleGroups.Modules,
            defaultChildRoute: AgingRoutes.Summary,
            children: [
                {
                    route: AgingRoutes.Summary,
                    label: 'Summary',
                },
                {
                    route: AgingRoutes.Claims,
                    label: 'Claims',
                    defaultChildRoute: 'search',
                    children: [
                        {
                            route: 'search',
                            defaultChildRoute: 'age',
                            children: [
                                {
                                    route: 'age',
                                    defaultChildRoute: '0',
                                },
                                {
                                    route: 'status',
                                    defaultChildRoute: '0',
                                },
                                {
                                    route: 'timelyFiling',
                                    defaultChildRoute: '0',
                                },
                                {
                                    route: 'lastWorked',
                                    defaultChildRoute: '0',
                                },
                            ],
                        },
                    ],
                },
                {
                    route: AgingRoutes.Worklist,
                    label: 'To-Do',
                    defaultChildRoute: TodoRoutes.Today,
                },
                {
                    route: AgingRoutes.AgingFiles,
                    label: 'Aging Files',
                    defaultChildRoute: 'summary',
                },
                {
                    route: AgingRoutes.ClaimPreviews,
                    label: 'Claim Preview Files',
                    defaultChildRoute: 'summary',
                    restrictUserAccess: (u: UserSessionResponse) =>
                        !u.pmSystem || u.pmSystem?.id !== PM_SYSTEM_IDS.REV_EHR,
                },
                {
                    route: AgingRoutes.Insights,
                    label: 'Insights',
                    defaultChildRoute: 'all',
                },
                {
                    route: AgingRoutes.UnassignedPayers,
                    defaultChildRoute: '',
                    hideFromNav: true,
                    restrictUserAccess: (u: UserSessionResponse) =>
                        (!this.flagService.isFlagActive(
                            FEATURE_FLAGS.EYE_CLOUD_PRO,
                        ) &&
                            !u.pmSystem) ||
                        u.pmSystem?.id !== PM_SYSTEM_IDS.EYE_CLOUD_PRO,
                },
            ],
        },
        {
            route: ModuleRoutes.Todo,
            label: 'To-Do',
            icon: 'fa-list-check',
            group: ModuleGroups.App,
            defaultChildRoute: TodoRoutes.Today,
            children: [],
        },
        {
            route: ModuleRoutes.Notifications,
            label: 'Notifications',
            icon: 'fa-solid fa-bell',
            hideFromNav: true,
            defaultChildRoute: NotificationRoutes.Open,
            children: [],
        },
        {
            route: ModuleRoutes.Admin,
            label: 'Admin',
            icon: 'fa-gear',
            group: ModuleGroups.App,
            defaultChildRoute: AdminRoutes.Account,
            children: [
                {
                    route: AdminRoutes.Account,
                    label: 'Account',
                    disableRedirectOnRoute: true,
                },
                // {
                //     route: AdminRoutes.Modules,
                //     label: 'Modules',
                //     disableRedirectOnRoute: true,
                // },
                {
                    route: AdminRoutes.Locations,
                    label: 'Locations',
                    defaultChildRoute: 'all',
                    restrictUserAccess: (u: UserSessionResponse) => {
                        return u.primaryRole?.id !== USER_ROLES.ADMIN.id;
                    },
                },
                {
                    route: AdminRoutes.Providers,
                    label: 'Providers',
                    defaultChildRoute: 'all',
                    restrictUserAccess: (u: UserSessionResponse) => {
                        return u.primaryRole?.id !== USER_ROLES.ADMIN.id;
                    },
                },
                {
                    route: AdminRoutes.Payers,
                    label: 'Payers',
                    defaultChildRoute: 'all',
                },
                {
                    route: AdminRoutes.Externals,
                    label: 'Externals',
                    defaultChildRoute: 'overview',
                },
                {
                    route: AdminRoutes.Users,
                    label: 'Users',
                    defaultChildRoute: 'all',
                    restrictUserAccess: (u: UserSessionResponse) => {
                        return (
                            u.primaryRole?.id !== USER_ROLES.ADMIN.id &&
                            u.primaryRole?.id !== USER_ROLES.BILLING_MANAGER.id
                        );
                    },
                },
            ],
        },
        {
            route: ModuleRoutes.Support,
            group: ModuleGroups.Footer,
        },
        {
            route: ModuleRoutes.Profile,
            label: 'User Account',
            icon: 'fa-circle-user fa-solid',
            group: ModuleGroups.Profile,
        },
        {
            route: '',
            label: '',
            icon: 'fa-circle-question fa-solid fa-xl',
            group: ModuleGroups.Utility,
            children: [
                {
                    route: '',
                    externalUrl: 'https://onespotbilling.zendesk.com/hc/en-us',
                    label: 'Knowledge Center',
                },
                {
                    route: ModuleRoutes.Support,
                    label: 'Support & Feedback',
                },
            ],
        },
        {
            route: ModuleRoutes.Profile,
            label: (u: UserSessionResponse) => {
                return `${u.firstName}`;
            },
            icon: 'fa-circle-user fa-solid fa-xl',
            group: ModuleGroups.Utility,
        },
    ];
    readonly store = inject(ModuleHistoryStore);
    readonly tableStore = inject(TableStore);
    readonly tabsStore = inject(TabsStore);
    url = new BehaviorSubject('');
    private appModules: AppModule[];
    private appModulesFlat: AppModule[];
    appModulesRegistered: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor(
        private router: Router,
        private authService: AuthService,
        private stateClearService: StateClearService,
        private flagService: FlagsService,
    ) {
        this.authService.getCurrentUser().subscribe((user) => {
            if (user) {
                const filteredRoutes = this.filterRoutePermissions(
                    this.orderModulesFromFlags(cloneDeep(this.APP_MODULES)),
                    user,
                );
                const labeledRoutes = this.evaluateLabels(filteredRoutes, user);
                this.appModules = this.extendRoutes(labeledRoutes);
                this.appModulesFlat = this.flattenRoutes(this.appModules);
                this.appModulesRegistered.next(true);
            }
        });
    }

    // TO-DO REMOVE WHEN WE REMOVE POSTING FF
    // constructor method should just use cloneDeep(this.APP_MODULES)
    // in filterRoutePermissions after.
    orderModulesFromFlags(routes: AppModule[]) {
        let isPostingFFEnabled: boolean = this.flagService.isFlagActive(
            FEATURE_FLAGS.POSTING_WORKLIST_TAB,
        );

        let sortedRoutes = routes;

        if (isPostingFFEnabled) {
            return sortedRoutes;
        } else {
            const scrubbingRoute = sortedRoutes[1];
            const agingRoute = sortedRoutes[2];
            sortedRoutes.splice(1, 1, agingRoute);
            sortedRoutes.splice(2, 1, scrubbingRoute);
            return sortedRoutes;
        }
    }

    private evaluateLabels(
        routes: AppModule[],
        user: UserSessionResponse,
    ): AppModule[] {
        for (const route of routes) {
            // Check if label exists and is a function, then call it with userSession
            if (route.label && typeof route.label === 'function') {
                route.label = route.label(user);
            }

            // If there are children, recursively evaluate their labels too
            if (route.children) {
                this.evaluateLabels(route.children, user);
            }
        }

        return routes;
    }

    private getAppModules(): BehaviorSubject<AppModule[]> {
        let modules = new BehaviorSubject([]);
        this.appModulesRegistered.subscribe((haveModules) => {
            if (haveModules) {
                modules.next(this.appModules);
            } else {
                modules.next([]);
            }
        });

        return modules;
    }

    private filterRoutePermissions(
        routes: AppModule[],
        user: UserSessionResponse,
    ): AppModule[] {
        return routes.reduce((acc: AppModule[], module: AppModule) => {
            // Check if the module should be removed based on restrictUserAccess
            if (module.restrictUserAccess && module.restrictUserAccess(user)) {
                // If true, skip adding this module to the accumulator
                return acc;
            } else {
                // If the module has children, recursively filter them too
                if (module.children) {
                    module = {
                        ...module, // Copy the module to avoid mutating the original object
                        children: this.filterRoutePermissions(
                            module.children,
                            user,
                        ), // Recursively filter children
                    };
                }
                // Add the possibly modified module (or unmodified if no children) to the accumulator
                acc.push(module);
                return acc;
            }
        }, []); // Start with an empty array as the accumulator
    }

    updateActiveModule(requestPath: string) {
        if (!this.appModulesFlat) {
            return;
        }

        const segments = this.splitPathToSegments(this.trimPath(requestPath));

        // Save Parent Module
        let activeParentModuleIndex = this.appModulesFlat.findIndex(
            (module) => {
                return this.trimPath(module.fullRoute) === segments[0];
            },
        );

        if (activeParentModuleIndex !== -1) {
            this.store.updateActiveParentModule(
                this.appModulesFlat[activeParentModuleIndex],
            );
        } else {
            this.store.deleteActiveParentModule();
        }

        // Save Child Module
        let activeChildModuleIndex = -1;

        if (segments.length >= 2) {
            activeChildModuleIndex = this.appModulesFlat.findIndex((module) => {
                let moduleSegments = this.splitPathToSegments(
                    this.trimPath(module.fullRoute),
                );

                if (moduleSegments.length >= 2) {
                    return moduleSegments[1] === segments[1];
                } else {
                    return false;
                }
            });
        }

        if (activeChildModuleIndex !== -1) {
            this.store.updateActiveChildModule(
                this.appModulesFlat[activeChildModuleIndex],
            );
        } else {
            this.store.deleteActiveChildModule();
        }
    }

    navigateToRoute(requestPath: string) {
        if (this.trimPath(requestPath) === 'login') {
            this.stateClearService.deleteAllSavedState();
            return;
        }

        if (!this.appModulesFlat) {
            return;
        }

        const segments = this.splitPathToSegments(this.trimPath(requestPath));
        const cleanRequestPath = this.joinPath(segments);
        const savedRoutes = getState(this.store).routes;
        let segmentLength = segments.length;
        let moduleIndex: number;

        // Loop through segments looking for module matches and save any found.
        while (segmentLength > 0) {
            const cleanPath = this.joinPath(segments);

            moduleIndex = this.appModulesFlat.findIndex((module) => {
                return this.trimPath(module.fullRoute) === cleanPath;
            });

            // Prepare for next loop through segment array
            segments.pop();
            segmentLength -= 1;

            // Save requestPath for found module as long as the requestPath and module path are not the same route.
            if (
                moduleIndex !== -1 &&
                this.trimPath(requestPath) !==
                    this.appModulesFlat[moduleIndex].fullRoute &&
                !this.appModulesFlat[moduleIndex].disableRedirectOnRoute
            ) {
                this.store.updateRoute(
                    requestPath,
                    this.appModulesFlat[moduleIndex].fullRoute,
                );
            }
        }

        // Determine if we need to navigate to a route.
        let navigationPath: string;

        const moduleRoutesIndex = this.appModulesFlat.findIndex((module) => {
            return this.trimPath(module.fullRoute) === cleanRequestPath;
        });

        // We found a module matching requestPath
        if (moduleRoutesIndex !== -1) {
            // See if we have a saved path.
            const savedRoutesIndex = savedRoutes.findIndex((route) => {
                return (
                    route.module ===
                    this.appModulesFlat[moduleRoutesIndex].fullRoute
                );
            });

            if (savedRoutesIndex !== -1) {
                // Navigate to found saved path.
                navigationPath = savedRoutes[savedRoutesIndex].route;
            } else {
                // Otherwise use defaultChildRoute.
                navigationPath = this.joinPath([
                    this.appModulesFlat[moduleRoutesIndex].fullRoute,
                    this.appModulesFlat[moduleRoutesIndex].defaultChildRoute,
                ]);
            }

            this.navigate(navigationPath);
        }
    }

    getRouteGroup(group: number): BehaviorSubject<AppModule[]> {
        let moduleGroup: BehaviorSubject<AppModule[]> = new BehaviorSubject([]);

        this.getAppModules().subscribe((modules) => {
            moduleGroup.next(
                this.appModules?.filter((item) => {
                    return item.group === group;
                }),
            );
        });

        return moduleGroup;
    }

    setActiveUrl(url: string): void {
        this.url.next(url);
    }

    private flattenRoutes(routes: AppModule[]) {
        let flatRoutes: AppModule[] = [];

        routes.forEach((item: AppModule) => {
            flatRoutes.push(item);

            // If the item has children, recursively flatten them and add to the result.
            if (Array.isArray(item.children) && item.children.length > 0) {
                let children = this.flattenRoutes(item.children);
                flatRoutes = flatRoutes.concat(children);
            }
        });

        return flatRoutes;
    }

    private extendRoutes(routes: AppModule[], parentRoute = '') {
        routes.forEach((item: AppModule) => {
            // Concatenate the current route with the parent route.
            let currentRoute = parentRoute
                ? `${parentRoute}/${item.route}`
                : item.route;

            item.fullRoute = currentRoute;

            // If the item has children, recursively update the route values for the children.
            if (Array.isArray(item.children) && item.children.length > 0) {
                item.children = this.extendRoutes(item.children, currentRoute);
            }
        });

        return routes;
    }

    private navigate(path: string): void {
        this.router.navigate([path]);
    }

    private trimPath(path: string) {
        path = trimStart(path, '/');
        path = trimEnd(path, '/');
        return path;
    }

    private splitPathToSegments(path: string): string[] {
        path = this.trimPath(path);
        return path.split('/');
    }

    private joinPath(segments: string[]): string {
        if (segments.length > 1) {
            return segments.join('/');
        }

        if (segments.length === 1) {
            return segments[0];
        }

        return undefined;
    }
}
